import { ValidationUtility } from "../validation-utility";
import { IValidatorType } from "../validator-interface";

import {
	IMobileAppDetailsPayload,
	IMobileAppStatusLogRow,
	IMobileAppStoreRow,
	MobileAppStoreKind,
} from "../community-admin/community-mobile-app";

/**
 * Branded mobile app — the STAFF side.
 *
 * Fulfilment is entirely manual: the Flutter app's white-label flavors are hardcoded across six
 * build files plus a CI allowlist, so a branded app is built by a person over weeks. These
 * endpoints are how that person records progress, and they are the only way a record ever leaves
 * MobileAppContractPending — the community-admin side can submit an intake but cannot advance it.
 *
 * Every write here also appends a CommunityMobileAppStatusLog row in the same transaction. That
 * pairing is the point of the table: a status that moved with no record of who moved it, when, or
 * why is exactly what the customer-facing history screen cannot render.
 */

//Req [GET] /platform/admin/community/mobile-app
export interface IPlatformAdminMobileAppGetRequest {
	communityId: string;
}

//Val [GET] /platform/admin/community/mobile-app
export const VPlatformAdminMobileAppGet: IValidatorType<IPlatformAdminMobileAppGetRequest> = {
	communityId: { function: ValidationUtility.validateStringFields, params: [1, -1], required: true },
};

//Res [GET] /platform/admin/community/mobile-app
export interface IPlatformAdminMobileAppResponse {
	configured: boolean;
	communityId: string;
	communityName: string;
	status: number | null;
	details: IMobileAppDetailsPayload | null;
	planId: string | null;
	priceAnnualAmount: number | null;
	priceCurrency: string | null;
	stripeInvoiceId: string | null;
	paidAt: number | null;
	paidOffline: boolean;
	termsAcceptedAt: number | null;
	termsAcceptedByUserId: string | null;
	stores: IMobileAppStoreRow[];
	statusLog: IMobileAppStatusLogRow[];
	createdAt: number | null;
	updatedAt: number | null;
}

/**
 * Advance the overall record.
 *
 * `note` is free text that lands on the log row. Optional, but it is the only place a staff
 * member can explain a non-obvious move — "customer asked to pause", "rebuilt after icon change"
 * — so the history screen is worth much less without it.
 */
//Req [PUT] /platform/admin/community/mobile-app/status
export interface IPlatformAdminMobileAppStatusRequest {
	communityId: string;
	/** A ModelStatus 100-105 value. 110-114 are per-store and belong on the store-status route. */
	status: number;
	note?: string;
}

//Val [PUT] /platform/admin/community/mobile-app/status
export const VPlatformAdminMobileAppStatus: IValidatorType<IPlatformAdminMobileAppStatusRequest> = {
	communityId: { function: ValidationUtility.validateStringFields, params: [1, -1], required: true },
	status: { function: ValidationUtility.validateNumber, params: [], required: true },
	note: { function: ValidationUtility.validateStringFields, params: [0, 1000], required: false },
};

//Res [PUT] /platform/admin/community/mobile-app/status
export interface IPlatformAdminMobileAppStatusResponse {
	status: number;
	/** True when this transition also set Community.isNetworkedPlus. See the route for why. */
	markedNetworkedPlus: boolean;
}

/**
 * Advance one store's review state, or open a new version.
 *
 * Keyed on (store, versionLabel) rather than a row id so staff can record a submission without
 * first having to look one up: submitting v1.1 to Apple creates the row, and every later move on
 * that same version updates it.
 */
//Req [PUT] /platform/admin/community/mobile-app/store-status
export interface IPlatformAdminMobileAppStoreStatusRequest {
	communityId: string;
	store: MobileAppStoreKind;
	/** As it appears in the store console, e.g. "1.0.2". */
	versionLabel: string;
	/** A ModelStatus 110-114 value. */
	status: number;
	/** Typically the store's rejection reason, pasted verbatim. */
	notes?: string;
}

//Val [PUT] /platform/admin/community/mobile-app/store-status
export const VPlatformAdminMobileAppStoreStatus: IValidatorType<IPlatformAdminMobileAppStoreStatusRequest> = {
	communityId: { function: ValidationUtility.validateStringFields, params: [1, -1], required: true },
	store: { function: ValidationUtility.validateNumber, params: [], required: true },
	versionLabel: { function: ValidationUtility.validateStringFields, params: [1, 50], required: true },
	status: { function: ValidationUtility.validateNumber, params: [], required: true },
	notes: { function: ValidationUtility.validateStringFields, params: [0, 2000], required: false },
};

//Res [PUT] /platform/admin/community/mobile-app/store-status
export interface IPlatformAdminMobileAppStoreStatusResponse {
	store: IMobileAppStoreRow;
}

/**
 * Mark the record paid without a Stripe charge.
 *
 * The escape hatch for any tier with no self-serve amount — a custom-priced Hub deal, or an
 * enterprise customer invoiced offline. "Payment gates development" is otherwise unsatisfiable
 * for them, and the record would sit at Contract Pending forever.
 *
 * Idempotent: marking an already-paid record paid again changes nothing and logs nothing.
 */
//Req [PUT] /platform/admin/community/mobile-app/mark-paid
export interface IPlatformAdminMobileAppMarkPaidRequest {
	communityId: string;
	/**
	 * The Plus tier that was paid for (`plus1`/`plus2`/`plus3`, a Plus product's `metadata.planId`
	 * — see `/stripe/platform/plans/networked-plus`). Required: an offline-paid community's token
	 * grant is resolved from this, and without it the token sync left a paying community on Free
	 * tokens (Spencer's review Q15).
	 */
	planId: string;
	/** Invoice reference, PO number, or whatever staff want findable later. */
	note?: string;
}

//Val [PUT] /platform/admin/community/mobile-app/mark-paid
export const VPlatformAdminMobileAppMarkPaid: IValidatorType<IPlatformAdminMobileAppMarkPaidRequest> = {
	communityId: { function: ValidationUtility.validateStringFields, params: [1, -1], required: true },
	planId: { function: ValidationUtility.validateStringFields, params: [1, 100], required: true },
	note: { function: ValidationUtility.validateStringFields, params: [0, 1000], required: false },
};

//Res [PUT] /platform/admin/community/mobile-app/mark-paid
export interface IPlatformAdminMobileAppMarkPaidResponse {
	paidAt: number;
	paidOffline: boolean;
	status: number;
}
